import { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, Trash2, RotateCcw, Check, AlertTriangle } from "lucide-react";

const MAX_SECONDS = 60;

function pickAudioMime() {
  const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4;codecs=mp4a.40.2", "audio/mp4", "audio/ogg;codecs=opus", "audio/mpeg"];
  for (const m of cands) if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
  return "";
}

function detectError(e) {
  const name = e?.name || "";
  if (name === "NotAllowedError") return { title: "Microphone access denied", msg: "Please allow microphone access in your browser settings, then try again." };
  if (name === "NotFoundError") return { title: "No microphone found", msg: "This device does not appear to have a microphone." };
  if (name === "NotReadableError") return { title: "Microphone in use", msg: "Another app is using the microphone. Close it and try again." };
  if (name === "SecurityError" || name === "TypeError") return { title: "Microphone blocked", msg: "Microphone access requires a secure (HTTPS) connection." };
  return { title: "Recording unavailable", msg: e?.message || "Unable to start recording." };
}

export default function VoiceRecorder({ onSaved, onCancel, maxSeconds = MAX_SECONDS }) {
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const [status, setStatus] = useState("idle"); // idle | recording | recorded
  const [seconds, setSeconds] = useState(0);
  const [preview, setPreview] = useState(null); // {blob, url, duration, ext, mime}
  const [playing, setPlaying] = useState(false);
  const [err, setErr] = useState(null);

  const stopStream = () => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
    streamRef.current = null;
  };

  useEffect(() => () => {
    stopStream();
    clearInterval(timerRef.current);
    if (preview?.url) URL.revokeObjectURL(preview.url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRec = async () => {
    setErr(null);
    if (typeof navigator === "undefined" || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErr({ title: "Recording not supported", msg: "This browser does not support voice recording." });
      return;
    }
    if (typeof window.MediaRecorder === "undefined") {
      setErr({ title: "Recording not supported", msg: "This browser does not support MediaRecorder." });
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = s;
      const mime = pickAudioMime();
      chunksRef.current = [];
      const rec = new MediaRecorder(s, mime ? { mimeType: mime } : undefined);
      rec.ondataavailable = (e) => e.data && e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const type = mime || rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const base = type.split(";")[0];
        let ext = "webm";
        if (base.includes("mp4") || base.includes("m4a") || base.includes("aac")) ext = "m4a";
        else if (base.includes("mpeg") || base.includes("mp3")) ext = "mp3";
        else if (base.includes("ogg")) ext = "ogg";
        setPreview({ blob, url: URL.createObjectURL(blob), duration: seconds, ext, mime: base });
        setStatus("recorded");
        stopStream();
      };
      rec.start(250);
      recorderRef.current = rec;
      setSeconds(0); setStatus("recording");
      timerRef.current = setInterval(() => setSeconds(s => {
        const n = s + 1;
        if (n >= maxSeconds) stopRec();
        return n;
      }), 1000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Voice recording failed:", e);
      setErr(detectError(e));
      stopStream();
    }
  };

  const stopRec = () => {
    clearInterval(timerRef.current);
    try { recorderRef.current && recorderRef.current.state !== "inactive" && recorderRef.current.stop(); } catch {}
  };

  const play = () => { audioRef.current?.play(); setPlaying(true); };
  const pause = () => { audioRef.current?.pause(); setPlaying(false); };
  const del = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null); setStatus("idle"); setSeconds(0); setPlaying(false);
  };
  const useIt = () => {
    if (!preview) return;
    const file = new File([preview.blob], `voice-${Date.now()}.${preview.ext}`, { type: preview.mime || preview.blob.type });
    onSaved(file, preview.duration);
  };

  const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  return (
    <div className="glass-subtle p-4 mt-3 bg-white/70" data-testid="voice-recorder">
      {err && (
        <div className="mb-3 flex items-start gap-2 text-sm text-neutral-800" data-testid="voice-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--accent)" }} />
          <div className="flex-1">
            <div className="font-medium">{err.title}</div>
            <div className="text-xs text-neutral-600">{err.msg}</div>
          </div>
          <button onClick={() => { setErr(null); startRec(); }} className="rounded-full px-3 py-1 text-xs text-white" style={{ background: "var(--accent)" }} data-testid="voice-try-again">Try Again</button>
        </div>
      )}
      {status === "idle" && (
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full grid place-items-center" style={{ background: "var(--accent)", color: "#fff" }}><Mic className="w-4 h-4" /></span>
          <div className="flex-1">
            <div className="text-sm font-medium">Record a voice message</div>
            <div className="text-xs text-neutral-500">Up to {maxSeconds}s</div>
          </div>
          <button onClick={startRec} className="rounded-full px-4 py-2 text-sm text-white" style={{ background: "var(--accent)" }} data-testid="voice-start">Start</button>
          {onCancel && <button onClick={onCancel} className="btn-ghost-glass rounded-full px-3 py-2 text-sm" data-testid="voice-cancel">Cancel</button>}
        </div>
      )}
      {status === "recording" && (
        <div className="flex items-center gap-3" data-testid="voice-recording">
          <span className="w-9 h-9 rounded-full bg-red-500 grid place-items-center text-white animate-pulse"><Mic className="w-4 h-4" /></span>
          <div className="flex-1">
            <div className="text-sm font-medium">Recording…</div>
            <div className="text-xs text-neutral-500 tabular-nums">{fmt(seconds)} / {fmt(maxSeconds)}</div>
          </div>
          <button onClick={stopRec} className="rounded-full px-4 py-2 text-sm text-white bg-neutral-900" data-testid="voice-stop"><Square className="w-3 h-3 inline mr-1" fill="white" /> Stop</button>
        </div>
      )}
      {status === "recorded" && preview && (
        <div className="fade-in" data-testid="voice-preview">
          <audio ref={audioRef} src={preview.url} onEnded={() => setPlaying(false)} className="hidden" />
          <div className="flex items-center gap-3">
            <button onClick={playing ? pause : play} className="w-9 h-9 rounded-full grid place-items-center text-white" style={{ background: "var(--accent)" }} data-testid="voice-play">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <div className="flex-1">
              <div className="text-sm font-medium">Voice message · {fmt(preview.duration)}</div>
              <div className="text-xs text-neutral-500">Tap play to review</div>
            </div>
            <button onClick={del} className="btn-ghost-glass rounded-full p-2" title="Delete" data-testid="voice-delete"><Trash2 className="w-4 h-4" /></button>
            <button onClick={() => { del(); startRec(); }} className="btn-ghost-glass rounded-full p-2" title="Record again" data-testid="voice-again"><RotateCcw className="w-4 h-4" /></button>
            <button onClick={useIt} className="rounded-full px-4 py-2 text-sm text-white flex items-center gap-1" style={{ background: "var(--accent)" }} data-testid="voice-use"><Check className="w-3 h-3" /> Use</button>
          </div>
        </div>
      )}
    </div>
  );
}
